import { type CompanyDto, type IngPayCheckDto, type IngPayConnectionDto, ingPayUpdateSchema, INTEGRATION_MODES, type IntegrationMode } from '@bqcrm/shared';
import { ActionIcon, Alert, Anchor, Badge, Button, CopyButton, Group, PasswordInput, Select, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { IconCheck, IconCopy, IconPlugConnected } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, errorText } from '../api';
import { ask } from '../confirm';
import { formatDateTime } from '../format';
import { notifyOk } from '../notify';
import { QueryState } from './QueryState';

export const ingPayBadge = (c: IngPayConnectionDto | undefined, failed = false) =>
  failed ? (
    <Badge variant="light" color="red" size="sm">
      ING Pay: nie udało się sprawdzić
    </Badge>
  ) : !c ? (
    <Badge variant="light" color="gray" size="sm">
      ING Pay: …
    </Badge>
  ) : c.mode === 'wylaczona' ? (
    <Badge variant="light" color="gray" size="sm">
      Płatności online: wyłączone
    </Badge>
  ) : (
    <Badge variant="light" color={c.active ? (c.mode === 'test' ? 'yellow' : 'green') : c.status === 'blad' ? 'red' : 'gray'} size="sm">
      ING Pay · {c.mode === 'test' ? 'sandbox' : INTEGRATION_MODES[c.mode]} · {c.active ? 'połączono' : c.status === 'blad' ? 'błąd' : 'nie sprawdzono'}
    </Badge>
  );

export const useIngPayConnection = (companyId: string, enabled = true) =>
  useQuery({ queryKey: ['integration-ing', companyId], queryFn: () => api<IngPayConnectionDto>(`/companies/${companyId}/integrations/ing-pay`), enabled });

/**
 * Płatności online ING Pay (docs/24): tryb (sandbox / produkcja), identyfikator klienta i sklepu, token autoryzacyjny
 * i klucz sklepu (tylko zapis — szyfrowane), adres powiadomień do wpisania w panelu ING Pay, „Sprawdź połączenie”.
 * Przyciski „Zapłać online” klient widzi dopiero po udanym sprawdzeniu.
 */
export function IngPaySettings({ company }: { company: CompanyDto }) {
  const qc = useQueryClient();
  const q = useIngPayConnection(company.id);
  const [mode, setMode] = useState<IntegrationMode>('wylaczona');
  const [merchantId, setMerchantId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [token, setToken] = useState('');
  const [serviceKey, setServiceKey] = useState('');
  const [check, setCheck] = useState<IngPayCheckDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!q.data) return;
    setMode(q.data.mode);
    setMerchantId(q.data.merchantId);
    setServiceId(q.data.serviceId);
  }, [q.data]);

  const url = `/companies/${company.id}/integrations/ing-pay`;
  const save = useMutation({
    mutationFn: async () => {
      const parsed = ingPayUpdateSchema.safeParse({ mode, merchantId, serviceId, token: token || undefined, serviceKey: serviceKey || undefined });
      if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? 'Sprawdź dane');
      await api<IngPayConnectionDto>(url, { method: 'PUT', body: parsed.data });
      return mode === 'wylaczona' ? null : api<IngPayCheckDto>(`${url}/check`, { method: 'POST' });
    },
    onSuccess: (r) => {
      setToken('');
      setServiceKey('');
      setError(null);
      setCheck(r);
      void qc.invalidateQueries({ queryKey: ['integration-ing', company.id] });
      if (!r) notifyOk('Płatności online wyłączone');
      else if (r.ok) notifyOk('Zapisano — połączenie z ING Pay działa');
    },
    onError: (e) => setError(errorText(e)),
  });
  const test = useMutation({
    mutationFn: () => api<IngPayCheckDto>(`${url}/check`, { method: 'POST' }),
    onSuccess: (r) => {
      setCheck(r);
      void qc.invalidateQueries({ queryKey: ['integration-ing', company.id] });
    },
    onError: (e) => setError(errorText(e)),
  });

  const c = q.data;
  if (!c)
    return (
      <QueryState isLoading={!q.error} error={q.error} lines={2}>
        {null}
      </QueryState>
    );
  const secretsMissing = (!c.tokenHint && !token) || (!c.serviceKeyHint && !serviceKey);
  return (
    <Stack gap="sm">
      <Group justify="space-between">
        {ingPayBadge(c)}
        {c.lastCheckAt && (
          <Text size="xs" c="dimmed">
            sprawdzono {formatDateTime(c.lastCheckAt)}
          </Text>
        )}
      </Group>
      {!c.keyConfigured && (
        <Alert color="red" title="Brak klucza szyfrowania na serwerze">
          Skontaktuj się z administratorem serwera — bez klucza szyfrowania nie zapiszesz tokenu i klucza sklepu.
        </Alert>
      )}
      <Text size="sm" c="dimmed">
        Klient zapłaci pro formę albo fakturę online (BLIK, karta, szybki przelew) z linku w e-mailu i ze strefy klienta, a sprzedawca w terenie — kodem QR. Wpłata zapisze się sama jak przelew. Dane znajdziesz w panelu ING Pay:
        Sklepy → Szczegóły → Dane do integracji.
      </Text>
      <Select
        label="Tryb"
        value={mode}
        onChange={(v) => v && setMode(v as IntegrationMode)}
        data={[
          { value: 'wylaczona', label: 'Wyłączona' },
          { value: 'test', label: 'Sandbox (testowe płatności)' },
          { value: 'produkcja', label: 'Produkcja (prawdziwe płatności)' },
        ]}
        description={mode === 'test' ? 'Panel sandbox.pay.ing.pl — pieniądze nie są pobierane (BLIK testowy 888000)' : mode === 'produkcja' ? 'Klienci płacą naprawdę; zwroty robi się w panelu ING Pay' : 'Klienci nie widzą przycisków „Zapłać online”'}
        allowDeselect={false}
      />
      <TextInput label="Identyfikator klienta" description="Merchant ID (litery i cyfry)" value={merchantId} onChange={(e) => setMerchantId(e.currentTarget.value)} autoComplete="off" />
      <TextInput label="Identyfikator sklepu" description="Service ID (UUID)" value={serviceId} onChange={(e) => setServiceId(e.currentTarget.value)} autoComplete="off" />
      <PasswordInput
        label="Token autoryzacyjny"
        description={c.tokenHint ? `Zapisany: ••••${c.tokenHint} — wpisz nowy, aby zastąpić` : 'Szyfrowany — nie da się go później odczytać, tylko zastąpić'}
        placeholder={c.tokenHint ? 'bez zmian' : 'wklej token z panelu ING Pay'}
        value={token}
        onChange={(e) => setToken(e.currentTarget.value)}
        autoComplete="off"
      />
      <PasswordInput
        label="Klucz sklepu"
        description={c.serviceKeyHint ? `Zapisany: ••••${c.serviceKeyHint} — wpisz nowy, aby zastąpić` : 'Do sprawdzania podpisu powiadomień o wpłatach; szyfrowany'}
        placeholder={c.serviceKeyHint ? 'bez zmian' : 'wklej klucz sklepu z panelu ING Pay'}
        value={serviceKey}
        onChange={(e) => setServiceKey(e.currentTarget.value)}
        autoComplete="off"
      />
      <Stack gap={2}>
        <Text size="sm" fw={500}>
          Adres powiadomień
        </Text>
        <Group gap="xs" wrap="nowrap">
          <Text size="xs" ff="monospace" style={{ wordBreak: 'break-all' }}>
            {c.notificationUrl}
          </Text>
          <CopyButton value={c.notificationUrl}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? 'Skopiowano' : 'Kopiuj'}>
                <ActionIcon variant="subtle" onClick={copy} aria-label="Kopiuj adres powiadomień">
                  {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        </Group>
        <Text size="xs" c="dimmed">
          Wpisz go w panelu ING Pay jako adres notyfikacji sklepu. Gdy powiadomienie nie dotrze, CRM sam sprawdza stan płatności co 10 minut i po powrocie klienta z bramki.
        </Text>
      </Stack>
      {error && <Alert color="red">{error}</Alert>}
      {check && (
        <Alert color={check.ok ? 'green' : 'red'} icon={<IconPlugConnected />}>
          {check.message}
          {check.ok && check.methods.length > 0 && <Text size="xs" mt={4}>Metody płatności sklepu: {check.methods.join(', ')}</Text>}
        </Alert>
      )}
      {c.status === 'blad' && !check && <Alert color="red">{c.lastError || 'Ostatnie połączenie nie powiodło się'}</Alert>}
      <Group justify="space-between">
        <Button variant="default" onClick={() => test.mutate()} loading={test.isPending} disabled={c.mode === 'wylaczona' || !c.tokenHint || !c.serviceKeyHint}>
          Sprawdź połączenie
        </Button>
        <Button
          onClick={async () => {
            if (mode === 'produkcja' && c.mode !== 'produkcja') {
              const ok = await ask('Tryb produkcyjny: klienci będą płacić prawdziwymi pieniędzmi z linków w e-mailach, strefy klienta i kodów QR w terenie. Upewnij się, że dane pochodzą z produkcyjnego panelu ING Pay (nie z sandboxa).', {
                title: 'Włączyć płatności produkcyjne?',
                confirm: 'Włącz produkcję',
                danger: true,
              });
              if (!ok) return;
            }
            save.mutate();
          }}
          loading={save.isPending}
          disabled={mode !== 'wylaczona' && (!merchantId || !serviceId || secretsMissing)}
        >
          Zapisz
        </Button>
      </Group>
      <Text size="xs" c="dimmed">
        Konto testowe:{' '}
        <Anchor href="https://sandbox.pay.ing.pl" target="_blank" rel="noopener" size="xs">
          sandbox.pay.ing.pl
        </Anchor>
        . Najpierw przetestuj w trybie „Sandbox”.
      </Text>
    </Stack>
  );
}
